import { ArrowUp, ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Cell as AriaCell,
  Column as AriaColumn,
  Row as AriaRow,
  Table as AriaTable,
  TableBody as AriaTableBody,
  TableHeader as AriaTableHeader,
  type ColumnProps,
  Group,
  type Key,
  type Selection,
  type SortDescriptor,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { Checkbox } from "./checkbox";
import { focusRing } from "./utils";

export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (item: T) => ReactNode;
  isRowHeader?: boolean;
  allowsSorting?: boolean;
  width?: ColumnProps["width"];
  minWidth?: number;
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  getRowId: (item: T) => Key;
  renderExpandedContent?: (item: T) => ReactNode;
  getRowClassName?: (item: T) => string | undefined;
  "aria-label": string;
  sortDescriptor?: SortDescriptor;
  onSortChange?: (descriptor: SortDescriptor) => void;
  renderEmptyState?: () => ReactNode;
  className?: string;
  selectionMode?: "multiple" | "single";
  selectedKeys?: "all" | Iterable<Key>;
  onSelectionChange?: (keys: Selection) => void;
}

const rowStyles = tv({
  extend: focusRing,
  base: "group/row relative cursor-default select-none -outline-offset-2 text-gray-900 disabled:text-gray-300 text-sm hover:bg-gray-100",
});

const cellStyles = tv({
  extend: focusRing,
  base: "border-b border-b-gray-200 group-last/row:border-b-0 px-1 py-1.5 md:p-2 -outline-offset-2",
});

const columnHeaderStyles = tv({
  extend: focusRing,
  base: "px-1 md:px-2 py-2 flex-1 flex gap-1 items-center overflow-hidden",
});

export function DataTable<T>({
  data,
  columns,
  getRowId,
  renderExpandedContent,
  getRowClassName,
  renderEmptyState,
  className,
  "aria-label": ariaLabel,
  sortDescriptor,
  onSortChange,
  selectionMode,
  selectedKeys,
  onSelectionChange,
}: DataTableProps<T>) {
  const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(new Set());

  const toggleExpanded = (key: Key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const hasExpandable = !!renderExpandedContent;
  const columnCount =
    columns.length + (hasExpandable ? 1 : 0) + (selectionMode ? 1 : 0);

  // react-aria throws if a table has no row-header column. Callers build
  // columns conditionally (e.g. per-player columns gated on team size), so the
  // designated row header can disappear at runtime. Guarantee one by falling
  // back to the first column when none is explicitly marked.
  const hasRowHeader = columns.some((col) => col.isRowHeader);

  return (
    <div
      className={twMerge(
        "w-full overflow-auto relative border bg-white rounded-lg",
        className,
      )}
    >
      <AriaTable
        aria-label={ariaLabel}
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        selectionMode={selectionMode}
        selectionBehavior={selectionMode ? "toggle" : undefined}
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        className="w-full min-w-full border-collapse bg-white"
      >
        <AriaTableHeader className="bg-navbar-background sticky top-0 z-10 border-b border-b-gray-200">
          {selectionMode === "multiple" && (
            <AriaColumn
              width={50}
              minWidth={50}
              className="cursor-default p-2 text-start text-sm font-semibold"
            >
              <Checkbox slot="selection" label="" />
            </AriaColumn>
          )}
          {hasExpandable && (
            <AriaColumn
              className="text-navbar-foreground w-6 min-w-6 p-0 text-sm font-semibold md:w-10 md:min-w-10 md:p-2"
              width={24}
              minWidth={24}
            />
          )}
          {columns.map((col, index) => (
            <AriaColumn
              key={col.id}
              id={col.id}
              isRowHeader={col.isRowHeader || (!hasRowHeader && index === 0)}
              allowsSorting={col.allowsSorting}
              width={col.width}
              minWidth={col.minWidth}
              className="text-navbar-foreground cursor-default text-start text-sm font-semibold focus-within:z-20 [&:hover]:z-20"
            >
              {({ allowsSorting, sortDirection }) => (
                <div className="flex items-center">
                  <Group
                    role="presentation"
                    tabIndex={-1}
                    className={columnHeaderStyles()}
                  >
                    <span className="truncate">{col.header}</span>
                    {allowsSorting && (
                      <span
                        className={twMerge(
                          "w-4 h-4 flex items-center justify-center transition",
                          sortDirection === "descending" && "rotate-180",
                        )}
                      >
                        {sortDirection && (
                          <ArrowUp
                            aria-hidden
                            className="text-navbar-foreground h-4 w-4"
                          />
                        )}
                      </span>
                    )}
                  </Group>
                </div>
              )}
            </AriaColumn>
          ))}
        </AriaTableHeader>
        <AriaTableBody
          renderEmptyState={
            renderEmptyState ??
            (() => (
              <div className="p-4 text-center text-gray-500 italic">
                No data.
              </div>
            ))
          }
        >
          {data.flatMap((item) => {
            const key = getRowId(item);
            const isExpanded = expandedKeys.has(key);
            const rows: ReactNode[] = [
              <AriaRow
                key={String(key)}
                id={key}
                className={rowStyles({
                  className: twMerge(
                    hasExpandable ? "cursor-pointer" : undefined,
                    getRowClassName?.(item),
                  ),
                })}
                {...(hasExpandable
                  ? { onAction: () => toggleExpanded(key) }
                  : {})}
              >
                {selectionMode && (
                  <AriaCell className={cellStyles()}>
                    <Checkbox slot="selection" label="" />
                  </AriaCell>
                )}
                {hasExpandable && (
                  <AriaCell
                    className={cellStyles({ className: "w-6 md:w-10" })}
                  >
                    <ChevronRight
                      size={16}
                      className={twMerge(
                        "transition-transform duration-200",
                        isExpanded && "rotate-90",
                      )}
                    />
                  </AriaCell>
                )}
                {columns.map((col) => (
                  <AriaCell key={col.id} className={cellStyles()}>
                    {col.cell(item)}
                  </AriaCell>
                ))}
              </AriaRow>,
            ];
            if (hasExpandable && isExpanded) {
              rows.push(
                <AriaRow
                  key={`${String(key)}-expanded`}
                  id={`${String(key)}-expanded`}
                  className="bg-gray-50"
                >
                  <AriaCell
                    colSpan={columnCount}
                    className="border-b border-b-gray-200"
                  >
                    {renderExpandedContent(item)}
                  </AriaCell>
                </AriaRow>,
              );
            }
            return rows;
          })}
        </AriaTableBody>
      </AriaTable>
    </div>
  );
}
